import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { Skeleton } from '../../../components/ui/Skeleton';
import { useCardIssuingConfig, useUpdateCardIssuingConfig } from '../../../hooks/useCards';

export function CardIssuingConfig() {
  const { data: config, isLoading } = useCardIssuingConfig();
  const updateConfig = useUpdateCardIssuingConfig();
  
  const [formData, setFormData] = useState({
    binPrefix: '',
    processorType: '',
    defaultDailyLimit: 5000,
    defaultTransactionLimit: 2000,
    defaultCreditLimit: 1000,
    virtualCardFee: 0,
    physicalCardFee: 25,
    annualFee: 0,
    monthlyInterestRate: 12.99,
    lateFeeRate: 2,
    virtualCardsEnabled: true,
    physicalCardsEnabled: false,
    creditCardsEnabled: false,
    isEnabled: false,
  });

  const [isEditing, setIsEditing] = useState(false);

  const handleEdit = () => {
    if (config) {
      setFormData({
        binPrefix: config.binPrefix || '',
        processorType: config.processorType || '',
        defaultDailyLimit: config.defaultDailyLimit,
        defaultTransactionLimit: config.defaultTransactionLimit,
        defaultCreditLimit: config.defaultCreditLimit,
        virtualCardFee: config.virtualCardFee,
        physicalCardFee: config.physicalCardFee,
        annualFee: config.annualFee,
        monthlyInterestRate: config.monthlyInterestRate,
        lateFeeRate: config.lateFeeRate,
        virtualCardsEnabled: config.virtualCardsEnabled,
        physicalCardsEnabled: config.physicalCardsEnabled,
        creditCardsEnabled: config.creditCardsEnabled,
        isEnabled: config.isEnabled,
      });
    }
    setIsEditing(true);
  };

  const handleSave = async () => {
    await updateConfig.mutateAsync({
      ...formData,
      defaultDailyLimit: formData.defaultDailyLimit * 100,
      defaultTransactionLimit: formData.defaultTransactionLimit * 100,
      defaultCreditLimit: formData.defaultCreditLimit * 100,
      virtualCardFee: formData.virtualCardFee * 100,
      physicalCardFee: formData.physicalCardFee * 100,
      annualFee: formData.annualFee * 100,
      monthlyInterestRate: formData.monthlyInterestRate / 100,
      lateFeeRate: formData.lateFeeRate / 100,
    });
    setIsEditing(false);
  };

  const handleToggleModule = async () => {
    await updateConfig.mutateAsync({ isEnabled: !config?.isEnabled });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Configuração de Cartões</h1>
          <p className="text-gray-500">Configure a emissão de cartões virtuais e físicos</p>
        </div>
        <div className="flex items-center gap-4">
          <Badge variant={config?.isEnabled ? 'success' : 'default'}>
            {config?.isEnabled ? 'Ativo' : 'Inativo'}
          </Badge>
          <Button
            variant={config?.isEnabled ? 'secondary' : 'primary'}
            onClick={handleToggleModule}
            disabled={updateConfig.isPending}
          >
            {config?.isEnabled ? 'Desativar Módulo' : 'Ativar Módulo'}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Processadora</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <>
                <div>
                  <label className="block text-sm font-medium mb-1">Prefixo BIN</label>
                  <Input
                    value={formData.binPrefix}
                    onChange={(e) => setFormData({ ...formData, binPrefix: e.target.value })}
                    placeholder="Ex: 123456"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo de Processadora</label>
                  <Input
                    value={formData.processorType}
                    onChange={(e) => setFormData({ ...formData, processorType: e.target.value })}
                    placeholder="Ex: VISA, MASTERCARD"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <span className="text-sm text-gray-500">Prefixo BIN</span>
                  <p className="font-medium">{config?.binPrefix || 'Não configurado'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Processadora</span>
                  <p className="font-medium">{config?.processorType || 'Não configurado'}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Funcionalidades</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.virtualCardsEnabled}
                    onChange={(e) => setFormData({ ...formData, virtualCardsEnabled: e.target.checked })}
                    className="rounded"
                  />
                  <span>Cartões Virtuais</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.physicalCardsEnabled}
                    onChange={(e) => setFormData({ ...formData, physicalCardsEnabled: e.target.checked })}
                    className="rounded"
                  />
                  <span>Cartões Físicos</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.creditCardsEnabled}
                    onChange={(e) => setFormData({ ...formData, creditCardsEnabled: e.target.checked })}
                    className="rounded"
                  />
                  <span>Cartões de Crédito</span>
                </label>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span>Cartões Virtuais</span>
                  <Badge variant={config?.virtualCardsEnabled ? 'success' : 'default'}>
                    {config?.virtualCardsEnabled ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span>Cartões Físicos</span>
                  <Badge variant={config?.physicalCardsEnabled ? 'success' : 'default'}>
                    {config?.physicalCardsEnabled ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span>Cartões de Crédito</span>
                  <Badge variant={config?.creditCardsEnabled ? 'success' : 'default'}>
                    {config?.creditCardsEnabled ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Limites Padrão</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <>
                <div>
                  <label className="block text-sm font-medium mb-1">Limite Diário (R$)</label>
                  <Input
                    type="number"
                    value={formData.defaultDailyLimit}
                    onChange={(e) => setFormData({ ...formData, defaultDailyLimit: parseFloat(e.target.value) })}
                    min={0}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Limite por Transação (R$)</label>
                  <Input
                    type="number"
                    value={formData.defaultTransactionLimit}
                    onChange={(e) => setFormData({ ...formData, defaultTransactionLimit: parseFloat(e.target.value) })}
                    min={0}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Limite de Crédito (R$)</label>
                  <Input
                    type="number"
                    value={formData.defaultCreditLimit}
                    onChange={(e) => setFormData({ ...formData, defaultCreditLimit: parseFloat(e.target.value) })}
                    min={0}
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <span className="text-sm text-gray-500">Limite Diário</span>
                  <p className="font-medium">{formatCurrency(config?.defaultDailyLimit || 0)}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Limite por Transação</span>
                  <p className="font-medium">{formatCurrency(config?.defaultTransactionLimit || 0)}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Limite de Crédito</span>
                  <p className="font-medium">{formatCurrency(config?.defaultCreditLimit || 0)}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Taxas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <>
                <div>
                  <label className="block text-sm font-medium mb-1">Taxa Cartão Virtual (R$)</label>
                  <Input
                    type="number"
                    value={formData.virtualCardFee}
                    onChange={(e) => setFormData({ ...formData, virtualCardFee: parseFloat(e.target.value) })}
                    min={0}
                    step={0.01}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Taxa Cartão Físico (R$)</label>
                  <Input
                    type="number"
                    value={formData.physicalCardFee}
                    onChange={(e) => setFormData({ ...formData, physicalCardFee: parseFloat(e.target.value) })}
                    min={0}
                    step={0.01}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Juros Mensal (%)</label>
                  <Input
                    type="number"
                    value={formData.monthlyInterestRate}
                    onChange={(e) => setFormData({ ...formData, monthlyInterestRate: parseFloat(e.target.value) })}
                    min={0}
                    step={0.01}
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <span className="text-sm text-gray-500">Taxa Cartão Virtual</span>
                  <p className="font-medium">{formatCurrency(config?.virtualCardFee || 0)}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Taxa Cartão Físico</span>
                  <p className="font-medium">{formatCurrency(config?.physicalCardFee || 0)}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Juros Mensal</span>
                  <p className="font-medium">{config?.monthlyInterestRate?.toFixed(2)}%</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end gap-4">
        {isEditing ? (
          <>
            <Button variant="secondary" onClick={() => setIsEditing(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={updateConfig.isPending}>
              {updateConfig.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </>
        ) : (
          <Button onClick={handleEdit}>Editar Configurações</Button>
        )}
      </div>
    </div>
  );
}

export default CardIssuingConfig;
